import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Scale, Building2, Briefcase, Check, ArrowRight } from "lucide-react";
import { Reveal, RevealStagger } from "@/components/vision/reveal";
import { EditorialHeading } from "./editorial-heading";
import { openWhatsAppContact } from "@/lib/whatsapp-contact";

const SEGMENTS = [
  { icon: Scale, key: "lawyer" },
  { icon: Building2, key: "firm" },
  { icon: Briefcase, key: "corporate" },
] as const;

export function AudienceSection() {
  const { t } = useTranslation();
  return (
    <section id="per-chi" className="relative overflow-hidden bg-background py-16 sm:py-24 md:py-28">
      <div className="relative mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <EditorialHeading
          index="No. 06"
          eyebrow={t("audience.eyebrow")}
          title={t("audience.title")}
          subtitle={t("audience.subtitle")}
        />

        <RevealStagger className="mt-12 grid gap-5 md:grid-cols-3" stagger={0.12} y={32} blur={8}>
          {SEGMENTS.map((s) => {
            const points = t(`audience.segments.${s.key}.points`, { returnObjects: true }) as string[];
            return (
              <div
                key={s.key}
                className="group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-card p-6 transition-all duration-500 hover:-translate-y-1 hover:border-primary/40 hover:shadow-xl sm:p-7"
              >
                <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-primary/5 transition-all duration-700 group-hover:scale-150 group-hover:bg-primary/10" />
                <div className="relative">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-primary-foreground transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3">
                    <s.icon className="h-6 w-6" />
                  </span>
                  <div className="mt-5">
                    <h3 className="font-display text-xl font-semibold">{t(`audience.segments.${s.key}.name`)}</h3>
                    <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-accent">
                      {t(`audience.segments.${s.key}.tagline`)}
                    </p>
                  </div>
                  <p className="mt-3 text-[14px] leading-relaxed text-muted-foreground sm:text-sm">
                    {t(`audience.segments.${s.key}.desc`)}
                  </p>
                  <ul className="mt-5 space-y-2.5">
                    {Array.isArray(points) &&
                      points.map((p) => (
                        <li key={p} className="flex items-start gap-2 text-[13px] text-foreground/85">
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                          <span>{p}</span>
                        </li>
                      ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </RevealStagger>

        <Reveal className="mt-10 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center" y={20} blur={6}>
          <Link
            to="/signup"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground shadow-glow transition-transform duration-300 hover:scale-[1.03]"
          >
            {t("audience.cta")} <ArrowRight className="h-4 w-4" />
          </Link>
          <button
            type="button"
            onClick={() => openWhatsAppContact()}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-border bg-card px-7 py-3.5 text-sm font-semibold text-foreground transition-colors hover:bg-muted"
          >
            {t("audience.ctaSecondary")} <ArrowRight className="h-4 w-4" />
          </button>
        </Reveal>
      </div>
    </section>
  );
}
